/**
 * EscolasPage — rota /escolas (RF-M7-002, RF-M7-007, US-08 AC1).
 * Lista escolas com filtros (UF, município, nome/INEP, suspeitas) e o grupo
 * especial "não identificada". Consome GET /escolas real (sem mocks estáticos).
 * ADR-038: a etiqueta da evidência de cada escola e o filtro Evidência (escolas com alguma visita com imagem /
 * escolas só com a planilha — sem a caixa de referências: elas não contam na escola).
 * ADR-041 (RN-M7-005, RN-M9-007): o filtro "Visitas" — a tela abre em "Só escolas com visitas" — e "Exportar CSV"
 * (a exportação por escola com os filtros da tela, acompanhada em Exportações). Nenhum dos dois vale no grupo "não
 * identificada" (visitas sem escola). Os filtros ficam no estado da tela, como os demais (emenda do Req 1.4).
 * Spec escolas-ordenacao-e-formatos: o cabeçalho ordena a lista inteira no servidor — o primeiro clique na direção
 * natural, o segundo inverte, a troca volta à página 1; as colunas das visitas só com o recorte "só com visitas"
 * (suspeitas e evidência também o fazem) e, no grupo "não identificada", só Nome, Visitas, Última visita e Suspeito. A
 * ordem fica no estado da tela e não vai à exportação. A data em DD/MM/AAAA; o município pelo nome (da API).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEscolas } from '../hooks/useConsulta';
import { useCriarExportacao } from '../hooks/useExportacoes';
import {
  EscolaFiltros,
  ORDENACOES_DAS_VISITAS,
  ORDENACOES_NAO_IDENTIFICADA,
  Ordem,
  OrdenacaoEscolas,
} from '../services/consulta-types';
import { filtroEscolasParaExportacao } from '../services/exportacoes-types';
import { nomesDiferem, Pager } from '../components/ConsultaShared';
import { EtiquetaEvidencia, FiltroEvidencia } from '../components/Evidencia';
import { formatarDataVisita } from '../components/format';

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

interface Ordenacao {
  readonly ordenarPor: OrdenacaoEscolas;
  readonly ordem: Ordem;
}

/** As colunas da tabela, na ordem da tela. */
const COLUNAS: ReadonlyArray<{ readonly titulo: string; readonly coluna: OrdenacaoEscolas }> = [
  { titulo: 'INEP', coluna: 'inep' },
  { titulo: 'Nome', coluna: 'nome' },
  { titulo: 'Município / UF', coluna: 'municipio' },
  { titulo: 'Visitas', coluna: 'visitas' },
  { titulo: 'Evidência', coluna: 'evidencia' },
  { titulo: 'Última visita', coluna: 'ultimaVisita' },
  { titulo: 'Suspeito', coluna: 'suspeito' },
];

/** A direção do primeiro clique: crescente nas colunas de texto e na evidência; decrescente nas contagens e datas. */
const DIRECAO_NATURAL: Readonly<Record<OrdenacaoEscolas, Ordem>> = {
  inep: 'asc',
  nome: 'asc',
  municipio: 'asc',
  visitas: 'desc',
  evidencia: 'asc',
  ultimaVisita: 'desc',
  suspeito: 'desc',
};

const DICA_SO_COM_VISITAS = "Ordenar por esta coluna só em 'Só escolas com visitas'";
const DICA_GRUPO = "Esta coluna não ordena o grupo 'não identificada'";

/** A coluna ordena com estes filtros? (o backend recusa as outras com 400) */
function podeOrdenar(coluna: OrdenacaoEscolas, f: EscolaFiltros): boolean {
  if (f.grupo === 'nao_identificada') {
    return ORDENACOES_NAO_IDENTIFICADA.includes(coluna);
  }
  // As colunas das visitas exigem o recorte das escolas com visita: o filtro "Visitas", suspeitas ou evidência.
  return (
    !ORDENACOES_DAS_VISITAS.includes(coluna) || f.comVisitas === true || f.suspeitas === true || f.evidencia !== undefined
  );
}

function Cabecalho(props: {
  readonly titulo: string;
  readonly coluna: OrdenacaoEscolas;
  readonly efetiva: Ordenacao;
  readonly filtros: EscolaFiltros;
  readonly onOrdenar: (coluna: OrdenacaoEscolas) => void;
}): JSX.Element {
  const { titulo, coluna, efetiva, filtros } = props;
  if (!podeOrdenar(coluna, filtros)) {
    const dica = filtros.grupo === 'nao_identificada' ? DICA_GRUPO : DICA_SO_COM_VISITAS;
    return (
      <th scope="col" aria-sort="none">
        <span className="th-sem-ordem" title={dica}>
          {titulo}
        </span>
      </th>
    );
  }
  const ativa = efetiva.ordenarPor === coluna;
  return (
    <th scope="col" aria-sort={ativa ? (efetiva.ordem === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="th-ordenar" onClick={() => props.onOrdenar(coluna)}>
        {titulo}
        {ativa && (
          <span className="th-seta" aria-hidden="true">
            {efetiva.ordem === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </button>
    </th>
  );
}

export function EscolasPage(): JSX.Element {
  // ADR-041 [OPEN-1]: a tela abre só com as escolas com visita; o catálogo inteiro fica a um clique.
  const [filtros, setFiltros] = useState<EscolaFiltros>({ comVisitas: true });
  const [page, setPage] = useState(1);
  // A ordem escolhida no cabeçalho; nula = o padrão da API (Nome, crescente; no grupo, Visitas, decrescente).
  const [ordenacao, setOrdenacao] = useState<Ordenacao | null>(null);
  const naoIdentificada = filtros.grupo === 'nao_identificada';
  const efetiva: Ordenacao =
    ordenacao ?? (naoIdentificada ? { ordenarPor: 'visitas', ordem: 'desc' } : { ordenarPor: 'nome', ordem: 'asc' });
  // Paginação por cursor (contrato): cursor = String(page); limit = tamanho fixo.
  const { pagina, carregando, erro } = useEscolas({
    ...filtros,
    // O grupo "não identificada" são visitas sem escola: o filtro "Visitas" não se aplica (o backend recusa).
    comVisitas: naoIdentificada ? undefined : filtros.comVisitas,
    ...(ordenacao ?? {}),
    cursor: page > 1 ? String(page) : undefined,
    limit: 50,
  });
  const { criar, enviando } = useCriarExportacao();
  const [pedido, setPedido] = useState<'pedida' | 'falhou' | null>(null);

  const atualizar = (parcial: Partial<EscolaFiltros>): void => {
    setPage(1);
    const novos = { ...filtros, ...parcial };
    setFiltros(novos);
    // Trocar de grupo, ou perder o recorte que a coluna ordenada exige, volta ao padrão.
    if (ordenacao && (novos.grupo !== filtros.grupo || !podeOrdenar(ordenacao.ordenarPor, novos))) {
      setOrdenacao(null);
    }
  };

  /** O clique no título: a coluna nova na direção natural; a mesma coluna, invertida. Volta à página 1. */
  const ordenarPor = (coluna: OrdenacaoEscolas): void => {
    setPage(1);
    setOrdenacao(
      efetiva.ordenarPor === coluna
        ? { ordenarPor: coluna, ordem: efetiva.ordem === 'asc' ? 'desc' : 'asc' }
        : { ordenarPor: coluna, ordem: DIRECAO_NATURAL[coluna] },
    );
  };

  /** RN-M9-007: a exportação por escola com os filtros da tela (sempre só as escolas com visita). */
  const exportarCsv = async (): Promise<void> => {
    setPedido(null);
    try {
      await criar({ formato: 'csv', escopo: 'escolas', filtro: filtroEscolasParaExportacao(filtros) });
      setPedido('pedida');
    } catch {
      setPedido('falhou');
    }
  };

  return (
    <div>
      <h1 className="page-title">Escolas</h1>
      <p className="page-subtitle">
        Navegue pelo acervo a partir da escola. Busque por UF, município, nome ou INEP.
      </p>

      <section className="card">
        <div className="filtros">
          {!naoIdentificada && (
            <div className="field">
              <label htmlFor="f-visitas">Visitas</label>
              <select
                id="f-visitas"
                value={filtros.comVisitas ? 'com' : 'todas'}
                onChange={(e) => atualizar({ comVisitas: e.target.value === 'com' })}
              >
                <option value="com">Só escolas com visitas</option>
                <option value="todas">Todas as escolas</option>
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="f-uf">UF</label>
            <select
              id="f-uf"
              value={filtros.uf ?? ''}
              onChange={(e) => atualizar({ uf: e.target.value || undefined })}
            >
              <option value="">Todas</option>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="f-mun">Município (IBGE)</label>
            <input
              id="f-mun"
              type="text"
              value={filtros.municipio ?? ''}
              onChange={(e) => atualizar({ municipio: e.target.value || undefined })}
            />
          </div>
          <div className="field">
            <label htmlFor="f-q">Nome ou INEP</label>
            <input
              id="f-q"
              type="text"
              value={filtros.q ?? ''}
              onChange={(e) => atualizar({ q: e.target.value || undefined })}
            />
          </div>
          <div className="field">
            <label htmlFor="f-susp">Somente suspeitas</label>
            <select
              id="f-susp"
              value={filtros.suspeitas ? 'true' : 'false'}
              onChange={(e) => atualizar({ suspeitas: e.target.value === 'true' })}
            >
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="f-grupo">Grupo</label>
            <select
              id="f-grupo"
              value={filtros.grupo ?? ''}
              onChange={(e) =>
                atualizar({
                  grupo: e.target.value === 'nao_identificada' ? 'nao_identificada' : undefined,
                })
              }
            >
              <option value="">Escolas identificadas</option>
              <option value="nao_identificada">Não identificada</option>
            </select>
          </div>
          <FiltroEvidencia
            id="f-evidencia"
            semReferencias
            valor={{ evidencia: filtros.evidencia }}
            onMudar={(v) => atualizar({ evidencia: v.evidencia })}
          />
        </div>
        {!naoIdentificada && (
          <div className="row-actions">
            <button className="btn btn-ghost" onClick={() => void exportarCsv()} disabled={enviando}>
              Exportar CSV
            </button>
            <span className="muted">Uma linha por escola com visita, com a última visita e o arquivo de origem.</span>
          </div>
        )}
        {pedido === 'pedida' && (
          <p className="notice" role="status">
            Exportação pedida — acompanhe em <Link to="/exportacoes">Exportações</Link>
          </p>
        )}
        {pedido === 'falhou' && <p className="notice">Não foi possível pedir a exportação.</p>}
      </section>

      <section className="card">
        <h2>Escolas</h2>
        {carregando && <p className="muted">Carregando…</p>}
        {erro && <p className="notice">Não foi possível carregar as escolas.</p>}
        {!carregando && !erro && (
          <>
            <table className="table">
              <thead>
                <tr>
                  {COLUNAS.map(({ titulo, coluna }) => (
                    <Cabecalho
                      key={coluna}
                      titulo={titulo}
                      coluna={coluna}
                      efetiva={efetiva}
                      filtros={filtros}
                      onOrdenar={ordenarPor}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {(pagina?.itens ?? []).map((e, i) => (
                  <tr key={e.inep ?? `ni-${i}`}>
                    <td className="mono">
                      {e.inep ? (
                        <Link to={`/escolas/${e.inep}`}>{e.inep}</Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {/* ADR-035: o oficial (a entidade INEP) e, quando difere, o nome como lido no termo. */}
                      <span className="nome-escola">
                        <span>{e.nome}</span>
                        {nomesDiferem(e.nomeTermo, e.nome) && (
                          <span className="nome-secundario">No termo: «{e.nomeTermo}»</span>
                        )}
                      </span>
                    </td>
                    <td>
                      {e.municipio ?? '—'} {e.uf ? `/ ${e.uf}` : ''}
                    </td>
                    <td>{e.qtdVisitas}</td>
                    <td>
                      <EtiquetaEvidencia evidencia={e.evidencia} />
                    </td>
                    <td>{e.ultimaVisita ? formatarDataVisita(e.ultimaVisita) : '—'}</td>
                    <td>
                      {e.temSuspeito ? (
                        <span className="pill pill-err">Sim</span>
                      ) : (
                        <span className="pill pill-ok">Não</span>
                      )}
                    </td>
                  </tr>
                ))}
                {(pagina?.itens.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      Nenhuma escola encontrada com os filtros atuais.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pager
              page={page}
              temProxima={pagina?.nextCursor !== null && pagina?.nextCursor !== undefined}
              onMudar={(p) => setPage(p)}
            />
          </>
        )}
      </section>
    </div>
  );
}
